import { useTranslation } from '@jobcost-monorepo/i18n';
import {
  Button,
  IconSvg,
  Loader,
  ModalDelete,
  SearchBar,
} from '@jobcost-monorepo/ui';
import { ReactElement, useEffect, useRef, useState } from 'react';
import {
  CrudID,
  IBaseModel,
  IListFetchParams,
  ListContext,
  MODULE,
  useList,
} from '../../common';
import CrudForm from '../CrudForm';
import GridFilters from './GridFilters';
import GridHeader from './GridHeader';
import GridRow from './GridRow';
import GridFooter from './GridFooter';
import { createRepository } from '../../dto/repositoryFactory';
import { cn, useBreakpoints } from '@jobcost-monorepo/utils';
import React from 'react';
import CardRow from './CardRow';

interface Props {
  moduleName: MODULE;
  updateStore?: boolean;
}

const Grid = <T extends IBaseModel, Q extends IListFetchParams>({
  moduleName,
  updateStore = true,
}: Props): ReactElement => {
  const { isSmall } = useBreakpoints();
  const { t } = useTranslation('commons');
  const containerRef = useRef<HTMLDivElement>(null);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [availableRows, setAvailableRows] = useState<number>(0);
  const rowHeight = 48; // Altezza massima delle righe, che corrisponde a max-h-12 (12 * 4 = 48px)
  const repository = createRepository<T, Q>(moduleName);
  const {
    fetchDataList,
    setFormElement,
    setParams,
    dataList,
    deleteElement,
    formElement,
    params,
    loading,
  } = useList<T, Q>({
    moduleName,
    repository,
    updateStore,
  });

  const isFiltersActive = repository.gridFiltersOptions.some((item) => {
    return params?.[item.linkedField];
  });

  const handleNew = (): void => {
    setFormOpen(true);
  };

  const handleDelete = async (id: CrudID): Promise<void> => {
    await deleteElement(id);
    await fetchDataList();
  };

  const handleChangeFiltersOpen = (): void => {
    setFiltersOpen(!filtersOpen);
  };

  const handleSearch = async (value?: string): Promise<void> => {
    await fetchDataList({
      ...params,
      skip: 0,
      search: value,
    } as Q);
  };

  const goToPage = async (page: number) => {
    if (page) {
      await fetchDataList({
        ...params,
        skip: (page - 1) * Number(availableRows),
      } as Q);
    }
  };

  const calculateAvailableRows = () => {
    if (containerRef.current) {
      const containerHeight = containerRef.current.clientHeight;
      const rows = Math.floor(containerHeight / rowHeight) - 1; //Togliamo l'header
      setAvailableRows(rows);
    }
  };

  useEffect(() => {
    if (!(availableRows > 0 && params?.take !== availableRows)) {
      return;
    }
    // @ts-ignore
    fetchDataList({ ...params, skip: 0, take: availableRows });
  }, [availableRows]);

  useEffect(() => {
    calculateAvailableRows();
    window.addEventListener('resize', calculateAvailableRows);
    return () => window.removeEventListener('resize', calculateAvailableRows);
  }, [containerRef.current]);

  return (
    <ListContext.Provider
      value={{
        fetchDataList,
        setFormElement,
        setParams,
        dataList,
        deleteElement,
        formElement,
        params,
        loading,
        setFormOpen,
        setDeleteModalOpen,
        moduleName,
        repository,
        updateStore,
        goToPage,
      }}
    >
      <div className="flex flex-col flex-grow mt-6 mb-2 mx-4 sm:mx-6 overflow-hidden">
        {formOpen ? (
          <CrudForm<T, Q> />
        ) : (
          <>
            <div
              className={cn(
                'h-max flex flex-wrap justify-between',
                isSmall ? 'flex-col items-start' : 'flex-row items-center',
              )}
            >
              <Loader showLoader={loading} />
              <SearchBar
                initialValue={params?.search}
                handleChangeSearch={handleSearch}
              />
              <div
                className={cn(
                  'items-center flex flex-row pb-2',
                  isSmall && 'w-full',
                )}
              >
                <Button
                  type="button"
                  onClick={handleNew}
                  className={cn(isSmall && 'w-full')}
                >
                  <IconSvg name={'plusIcon'} />
                  {t('grid.new')}
                </Button>
                {repository.gridFiltersOptions?.length > 0 && (
                  <Button
                    type="button"
                    onClick={handleChangeFiltersOpen}
                    className={cn(isSmall && 'w-full', 'ml-3')}
                  >
                    <IconSvg
                      name={isFiltersActive ? 'funnelIcon' : 'filterVertical'}
                    />
                    <div className="ml-1" />
                    {t('filters.title')}
                  </Button>
                )}
              </div>
            </div>

            {isSmall ? (
              <div className="flex flex-col flex-grow overflow-y-auto">
                {dataList.data?.length > 0 ? (
                  dataList.data?.map((element, index) => (
                    <CardRow<T, Q>
                      key={element.id}
                      element={element}
                      className={
                        index % 2
                          ? 'bg-violet2 dark:bg-transparent rounded-lg border-purple70 dark:border-grey border-[1px] mt-2'
                          : 'bg-violet5 dark:bg-darkGrey rounded-lg border-purple70 dark:border-grey border-[1px] mt-2'
                      }
                    />
                  ))
                ) : (
                  <div className="w-full flex items-center justify-center h-[calc(100%-50px)]">
                    {t('noRecordFound')}
                  </div>
                )}
              </div>
            ) : (
              <div
                ref={containerRef}
                className="rounded-lg border-purple70 dark:border-grey border-[1px] size-full overflow-y-auto"
              >
                <table className="w-full">
                  <GridHeader key="header" repository={repository} />
                  {dataList.data?.length > 0 && (
                    <tbody className="size-full">
                      {dataList.data?.map((element, index) => (
                        <GridRow<T, Q>
                          key={element.id}
                          element={element}
                          className={
                            index % 2
                              ? 'bg-violet2 dark:bg-transparent'
                              : 'bg-violet5 dark:bg-darkGrey'
                          }
                        />
                      ))}
                    </tbody>
                  )}
                </table>
                {!dataList.data?.length && (
                  <div className="w-full flex items-center justify-center h-[calc(100%-50px)]">
                    {t('noRecordFound')}
                  </div>
                )}
              </div>
            )}
            <GridFooter<T, Q> />
            {filtersOpen && (
              <GridFilters<T, Q>
                isOpen={filtersOpen}
                handleClose={handleChangeFiltersOpen}
              />
            )}
            {formElement && formElement.id && (
              <ModalDelete
                isOpen={deleteModalOpen}
                handleClose={() => setDeleteModalOpen(false)}
                handleDelete={() => handleDelete(formElement.id)}
                loader={loading}
              />
            )}
          </>
        )}
      </div>
    </ListContext.Provider>
  );
};

export default Grid;
